import ProductSubscription from "@aihCustomBlueprint/Components/ProductSubscription";
import ProductPartNumbers from "@aihCustomBlueprint/Overrides/Components/ProductPartNumbers";
import { AllProductSubscriptionStyles } from "@aihCustomBlueprint/Widgets/ProductSubscription/AllProductSubscriptions";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import CardContainer from "@insite/content-library/Components/CardContainer";
import ProductBrand from "@insite/content-library/Components/ProductBrand";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import React, { ReactNode } from "react";
import { connect } from "react-redux";

interface OwnProps {
    product: ProductModel;
    productInfo?: ProductInfo | null;
    onClick?: React.EventHandler<React.MouseEvent>;
    styles: AllProductSubscriptionStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    showPrice: state.context.showPricing,
    showInventory: state.context.showInventory,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductSubscriptionItem: React.FC<Props> = ({
    product,
    productInfo,
    showPrice = true,
    showInventory = true,
    onClick,
    styles,
}) => {
    const renderInStockProducts = (children: ReactNode, productContext: ProductContextModel) => {
        return <ProductContext.Provider value={productContext}>{children}</ProductContext.Provider>;
    };

    if (productInfo) {
        const productContext: ProductContextModel = {
            product,
            productInfo,
        };

        const overriddenProps = {
            showPrice,
            showAddToList: true,
            showBrand: true,
            showTitle: true,
            showPartNumbers: true,
            showAvailability: showInventory,
            showAttributes: true,
            showImage: true,
            showCompare: true,
        };

        const PLP = (
            <CardContainer extendedStyles={styles.cardContainerStyles}>
                <ProductListProductCard {...overriddenProps} />
            </CardContainer>
        );
        return renderInStockProducts(PLP, productContext);
    }

    return (
        <GridContainer {...styles.gridContainer}>
            <GridItem {...styles.leftColumnGridItem}>
                <Clickable
                    {...styles.linkWrappingImage}
                    href={product.canonicalUrl}
                    onClick={onClick}
                    data-test-selector="subscriptionProductImage"
                >
                    <Img
                        height={209}
                        width={209}
                        src={product.smallImagePath}
                        altText={product.imageAltText}
                        {...styles.img}
                    />
                </Clickable>
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <GridContainer {...styles.rightColumnGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <GridContainer {...styles.container}>
                            {product.brand && (
                                <GridItem {...styles.brandGridItem}>
                                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                                </GridItem>
                            )}
                            <GridItem {...styles.descriptionGridItem}>
                                <Link
                                    {...styles.productDetailLink}
                                    href={product.canonicalUrl}
                                    alt={`See product details for ${product.productTitle}`}
                                    onClick={onClick}
                                    data-test-selector="productDescriptionLink"
                                >
                                    {product.productTitle}
                                </Link>
                            </GridItem>
                            <GridItem {...styles.partNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.partNumbersStyles}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>

                    <GridItem {...styles.actionsGridItem}>
                        <StyledWrapper {...styles.subscribeButtonBlock} data-test-selector="subscribeUnsubscribeBlock">
                            <ProductSubscription erpNumber={product.productNumber} productSubscribed={true} />
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(ProductSubscriptionItem);
